import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { themes, layouts, fontSizes, applyTheme, applyFontSize, loadUserTheme, saveUserTheme } from './theme';

const API = '/api';

// ── Auth context ──────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
function useAuth() { return useContext(AuthContext); }

// ── Theme context ─────────────────────────────────────────────────────────────
const ThemeContext = createContext(null);
function useTheme() { return useContext(ThemeContext); }

// ── API helper ────────────────────────────────────────────────────────────────
function api(path, opts = {}) {
  const token = localStorage.getItem('udbud_token');
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers },
    ...opts,
    body: opts.body ? (typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body)) : undefined,
  }).then(r => r.json());
}

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = v => v ? new Intl.NumberFormat('da-DK').format(v) : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('da-DK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysLeft = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const STATUS = {
  draft: { label: 'Kladde', color: 'var(--text-3)' },
  open: { label: 'Åbent', color: 'var(--green)' },
  evaluating: { label: 'Evaluering', color: 'var(--orange)' },
  awarded: { label: 'Tildelt', color: 'var(--accent)' },
  cancelled: { label: 'Annulleret', color: 'var(--red)' },
};

const PASSENGER_LABEL = { elderly: 'Ældre', disabled: 'Handicappede', general: 'Generel', school: 'Skolekørsel', mixed: 'Blandet' };
const TENDER_TYPE_LABEL = { standard: 'Standard', framework: 'Rammeaftale', urgent: 'Akutudbud' };
const ROLE_INFO = {
  fleetos_admin: { label: 'FleetOS Admin', icon: '⚙️', color: 'var(--accent)' },
  org_admin: { label: 'Admin', icon: '👑', color: 'var(--orange)' },
  case_handler: { label: 'Sagsbehandler', icon: '📋', color: 'var(--green)' },
  reader: { label: 'Læser', icon: '👁', color: 'var(--text-3)' },
};

// ══════════════════════════════════════════════════════════════════════════════
// UI PRIMITIVES
// ══════════════════════════════════════════════════════════════════════════════

function Badge({ status }) {
  const s = STATUS[status] || { label: status, color: 'var(--text-3)' };
  return (
    <span style={{ background: s.color + '18', color: s.color, border: `1px solid ${s.color}30`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

function Card({ children, style = {}, onClick, hover = false }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="fade-in"
      style={{
        background: hov && hover ? 'var(--bg-3)' : 'var(--bg-2)',
        border: `1px solid ${hov && hover ? 'var(--border)' : 'var(--border-light)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: 20,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: hov && hover ? 'var(--shadow)' : 'var(--shadow-sm)',
        transform: hov && hover ? 'translateY(-1px)' : 'none',
        ...style
      }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, variant = 'primary', size = 'md', disabled = false, icon = null, style = {} }) {
  const [hov, setHov] = useState(false);
  const variants = {
    primary: { bg: hov ? 'var(--accent-2)' : 'var(--accent)', color: '#fff', border: 'transparent' },
    secondary: { bg: hov ? 'var(--bg-4)' : 'var(--bg-3)', color: 'var(--text)', border: 'var(--border)' },
    ghost: { bg: hov ? 'var(--bg-3)' : 'transparent', color: 'var(--text-2)', border: 'transparent' },
    danger: { bg: hov ? '#dc2626' : 'var(--red-bg)', color: hov ? '#fff' : 'var(--red)', border: 'var(--red)' },
    success: { bg: hov ? '#16a34a' : 'var(--green-bg)', color: hov ? '#fff' : 'var(--green)', border: 'var(--green)' },
  };
  const sizes = {
    sm: { padding: '5px 12px', fontSize: 12 },
    md: { padding: '8px 16px', fontSize: 13 },
    lg: { padding: '11px 22px', fontSize: 14 },
    xl: { padding: '14px 28px', fontSize: 15 },
  };
  const v = variants[variant] || variants.primary;
  const s = sizes[size] || sizes.md;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: v.bg, color: v.color,
        border: `1px solid ${v.border}`,
        borderRadius: 'var(--radius)',
        ...s,
        fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 7,
        fontFamily: 'inherit', whiteSpace: 'nowrap',
        ...style
      }}>
      {icon && <span style={{ fontSize: '1.1em' }}>{icon}</span>}
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '', required = false, hint = '' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ display: 'block', fontWeight: 500, fontSize: 12, color: 'var(--text-2)', marginBottom: 6, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          {label}{required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)', background: 'var(--bg-3)',
          color: 'var(--text)', fontSize: 'var(--font-base)', outline: 'none',
          fontFamily: 'inherit', boxSizing: 'border-box',
        }} />
      {hint && <p style={{ margin: '5px 0 0', fontSize: 11, color: 'var(--text-3)' }}>{hint}</p>}
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder = '' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontWeight: 500, fontSize: 12, color: 'var(--text-2)', marginBottom: 6, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{label}</label>}
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text)', fontSize: 'var(--font-base)', outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontWeight: 500, fontSize: 12, color: 'var(--text-2)', marginBottom: 6, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text)', fontSize: 'var(--font-base)', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Spinner() {
  return <div style={{ width: 18, height: 18, border: '2px solid var(--border)', borderTop: '2px solid var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />;
}

function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {label && <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>}
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || 'var(--accent)', letterSpacing: '-0.03em' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 3, fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Tema-vælger popup ──────────────────────────────────────────────────────────
function ThemePicker({ onClose }) {
  const { activeTheme, activeFont, setTheme, setFont } = useTheme();
  return (
    <div className="fade-in" style={{ position: 'fixed', top: 60, right: 20, width: 320, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', padding: 20, zIndex: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Tilpas udseende</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>Farvetema</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {Object.entries(themes).map(([key, t]) => (
          <button key={key} onClick={() => setTheme(key)}
            style={{
              padding: '10px 12px', borderRadius: 'var(--radius)', border: `2px solid ${activeTheme === key ? 'var(--accent)' : 'var(--border)'}`,
              background: activeTheme === key ? 'var(--accent-bg)' : 'var(--bg-3)',
              color: activeTheme === key ? 'var(--accent)' : 'var(--text-2)',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            <span>{t.icon}</span>{t.name}
            {activeTheme === key && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
          </button>
        ))}
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>Skriftstørrelse</p>
      <div style={{ display: 'flex', gap: 8 }}>
        {Object.entries(fontSizes).map(([key, f]) => (
          <button key={key} onClick={() => setFont(key)}
            style={{
              flex: 1, padding: '8px', borderRadius: 'var(--radius)', border: `2px solid ${activeFont === key ? 'var(--accent)' : 'var(--border)'}`,
              background: activeFont === key ? 'var(--accent-bg)' : 'var(--bg-3)',
              color: activeFont === key ? 'var(--accent)' : 'var(--text-2)',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12,
            }}>
            {f.name}
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LOGIN & SIGNUP
// ══════════════════════════════════════════════════════════════════════════════

function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState('login'); // login | signup | forgot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Signup
  const [sg, setSg] = useState({
    org_type: 'carrier', name: '', cvr: '', contact_name: '',
    contact_email: '', contact_phone: '', city: '', password: '', password2: ''
  });

  const handleLogin = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    const r = await api('/udbud/auth/login', { method: 'POST', body: { email, password } });
    if (r.ok) {
      localStorage.setItem('udbud_token', r.token);
      onAuth(r.user);
    } else {
      setError(r.error || 'Fejl ved login');
    }
    setLoading(false);
  };

  const handleSignup = async e => {
    e.preventDefault();
    if (sg.password !== sg.password2) { setError('Adgangskoderne matcher ikke'); return; }
    if (sg.password.length < 8) { setError('Adgangskode skal være mindst 8 tegn'); return; }
    setLoading(true); setError('');
    const r = await api('/udbud/organisations/signup', {
      method: 'POST',
      body: { org_type: sg.org_type, name: sg.name, cvr: sg.cvr, contact_name: sg.contact_name, contact_email: sg.contact_email, contact_phone: sg.contact_phone, city: sg.city, password: sg.password }
    });
    if (r.ok) {
      localStorage.setItem('udbud_token', r.token);
      onAuth(r.user);
    } else {
      setError(r.error || 'Fejl ved registrering');
    }
    setLoading(false);
  };

  const handleForgot = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    await api('/udbud/auth/forgot', { method: 'POST', body: { email } });
    setSuccess('Hvis emailen findes, er der sendt et link til nulstilling.');
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex' }}>
      {/* Venstre — branding */}
      <div style={{
        display: 'none', flex: 1, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)',
        padding: 48, flexDirection: 'column', justifyContent: 'space-between',
        '@media(min-width:768px)': { display: 'flex' }
      }} className="auth-left">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
            <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🚕</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>FleetOS</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Udbudsportal</div>
            </div>
          </div>
          <h1 style={{ color: '#fff', fontSize: 36, fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.03em', maxWidth: 400 }}>
            Danmarks smarteste platform for taxaudbud
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 16, marginTop: 16, maxWidth: 380, lineHeight: 1.7 }}>
            Kommuner og vognmænd mødes digitalt — fra udbud til kontrakt og afregning.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { icon: '🏛️', text: 'Kommuner publicerer udbud digitalt' },
            { icon: '🚕', text: 'Vognmænd afgiver tilbud solo eller i konsortium' },
            { icon: '📊', text: 'Automatisk scoring og EAN-fakturering' },
            { icon: '🔗', text: 'Direkte integration med FleetOS dispatch' },
          ].map(f => (
            <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{f.icon}</div>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 500 }}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Højre — form */}
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '40px 32px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'auto' }}>
        {/* Mobile logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
          <div style={{ width: 36, height: 36, background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🚕</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>FleetOS</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Udbudsportal</div>
          </div>
        </div>

        {mode === 'login' && (
          <div className="fade-in">
            <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Velkommen tilbage</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 32 }}>Log ind for at fortsætte til udbudsportalen</p>

            {error && <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 20, color: 'var(--red)', fontSize: 13, fontWeight: 500 }}>⚠️ {error}</div>}

            <form onSubmit={handleLogin}>
              <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="din@email.dk" required />
              <Input label="Adgangskode" value={password} onChange={setPassword} type="password" placeholder="••••••••" required />
              <div style={{ textAlign: 'right', marginTop: -10, marginBottom: 20 }}>
                <button type="button" onClick={() => { setMode('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Glemt adgangskode?</button>
              </div>
              <Btn style={{ width: '100%', justifyContent: 'center' }} size="xl" disabled={loading}>
                {loading ? <><Spinner /> Logger ind...</> : 'Log ind →'}
              </Btn>
            </form>

            <Divider label="Ny bruger?" />
            <Btn variant="secondary" size="lg" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setMode('signup'); setError(''); }}>
              Opret konto som vognmand
            </Btn>
            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-3)', marginTop: 16 }}>
              Er du kommune? Kontakt FleetOS for adgang.
            </p>
          </div>
        )}

        {mode === 'signup' && (
          <div className="fade-in">
            <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Opret konto</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 32 }}>Som vognmand eller kørselskontor</p>

            {error && <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 20, color: 'var(--red)', fontSize: 13, fontWeight: 500 }}>⚠️ {error}</div>}

            <form onSubmit={handleSignup}>
              <Select label="Virksomhedstype" value={sg.org_type} onChange={v => setSg({ ...sg, org_type: v })} options={[{ value: 'carrier', label: '🚕 Vognmand / Taxaselskab' }, { value: 'transport_office', label: '🏢 Kørselskontor' }]} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Input label="Firmanavn" value={sg.name} onChange={v => setSg({ ...sg, name: v })} placeholder="Taxa ApS" required />
                <Input label="CVR-nummer" value={sg.cvr} onChange={v => setSg({ ...sg, cvr: v })} placeholder="12345678" />
                <Input label="Ansvarlig leder" value={sg.contact_name} onChange={v => setSg({ ...sg, contact_name: v })} required />
                <Input label="Telefon" value={sg.contact_phone} onChange={v => setSg({ ...sg, contact_phone: v })} placeholder="+45 12 34 56 78" />
              </div>
              <Input label="Email (bruges til login)" value={sg.contact_email} onChange={v => setSg({ ...sg, contact_email: v })} type="email" placeholder="din@email.dk" required />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Input label="Adgangskode" value={sg.password} onChange={v => setSg({ ...sg, password: v })} type="password" placeholder="Min. 8 tegn" required />
                <Input label="Gentag adgangskode" value={sg.password2} onChange={v => setSg({ ...sg, password2: v })} type="password" placeholder="Gentag" required />
              </div>
              <Btn style={{ width: '100%', justifyContent: 'center' }} size="xl" disabled={loading}>
                {loading ? <><Spinner /> Opretter...</> : 'Opret konto →'}
              </Btn>
            </form>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button onClick={() => { setMode('login'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>← Tilbage til login</button>
            </div>
          </div>
        )}

        {mode === 'forgot' && (
          <div className="fade-in">
            <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Nulstil adgangskode</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 32 }}>Vi sender dig et link via email</p>
            {success && <div style={{ background: 'var(--green-bg)', border: '1px solid var(--green)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 20, color: 'var(--green)', fontSize: 13, fontWeight: 500 }}>✓ {success}</div>}
            <form onSubmit={handleForgot}>
              <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="din@email.dk" required />
              <Btn style={{ width: '100%', justifyContent: 'center' }} size="xl" disabled={loading || !!success}>
                {loading ? <><Spinner /> Sender...</> : 'Send nulstillings-link'}
              </Btn>
            </form>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>← Tilbage til login</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TOPBAR
// ══════════════════════════════════════════════════════════════════════════════

function Topbar({ activeView, setView, showTheme, setShowTheme }) {
  const { user, logout } = useAuth();
  const role = ROLE_INFO[user?.role] || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();

  const navItems = [
    { id: 'tenders', label: 'Udbud', icon: '📋' },
    ...(user?.role === 'fleetos_admin' || user?.role === 'org_admin' ? [{ id: 'organisations', label: 'Organisationer', icon: '🏛️' }] : []),
    { id: 'consortia', label: 'Konsortier', icon: '🤝' },
    ...(user?.role === 'fleetos_admin' ? [{ id: 'invoices', label: 'Fakturaer', icon: '🧾' }] : []),
    ...(user?.role === 'fleetos_admin' || user?.role === 'org_admin' ? [{ id: 'team', label: 'Team', icon: '👥' }] : []),
  ];

  return (
    <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', height: 58, display: 'flex', alignItems: 'center', padding: '0 20px', gap: 8, position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 16, cursor: 'pointer' }} onClick={() => setView('tenders')}>
        <div style={{ width: 30, height: 30, background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🚕</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: '-0.01em', lineHeight: 1 }}>FleetOS</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', lineHeight: 1 }}>Udbudsportal</div>
        </div>
      </div>

      {/* Nav */}
      {navItems.map(n => (
        <button key={n.id} onClick={() => setView(n.id)}
          style={{
            background: activeView === n.id ? 'var(--accent-bg)' : 'none',
            border: 'none', borderRadius: 'var(--radius)',
            padding: '6px 12px', color: activeView === n.id ? 'var(--accent)' : 'var(--text-2)',
            fontSize: 13, fontWeight: activeView === n.id ? 600 : 500,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6
          }}>
          <span>{n.icon}</span>{n.label}
        </button>
      ))}

      <div style={{ flex: 1 }} />

      {/* Theme toggle */}
      <button onClick={() => setShowTheme(s => !s)}
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--text-2)', fontSize: 16, cursor: 'pointer' }}
        title="Tilpas udseende">
        🎨
      </button>

      {/* User menu */}
      <div style={{ position: 'relative' }} ref={menuRef}>
        <button onClick={() => setMenuOpen(s => !s)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 12px', cursor: 'pointer', color: 'var(--text)' }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent-bg)', border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{user?.name}</div>
            <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{role.label}</div>
          </div>
          <span style={{ fontSize: 10, color: 'var(--text-3)' }}>▾</span>
        </button>
        {menuOpen && (
          <div className="fade-in" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, width: 220, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden', zIndex: 200 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{user?.email}</div>
              <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--accent-bg)', color: 'var(--accent)', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                {role.icon} {role.label}
              </div>
            </div>
            <div style={{ padding: 6 }}>
              <button onClick={() => { setMenuOpen(false); setShowTheme(true); }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}>
                🎨 Tilpas udseende
              </button>
              <button onClick={() => { setMenuOpen(false); logout(); }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'none', border: 'none', color: 'var(--red)', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}>
                🚪 Log ud
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TENDER LIST
// ══════════════════════════════════════════════════════════════════════════════

function TenderList({ onSelect, onNew }) {
  const { user } = useAuth();
  const [tenders, setTenders] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/tenders${filter !== 'all' ? `?status=${filter}` : ''}`)
      .then(d => { setTenders(Array.isArray(d) ? d : []); setLoading(false); });
  }, [filter]);

  const filters = [
    { v: 'open', l: 'Åbne', icon: '🟢' },
    { v: 'evaluating', l: 'Evaluering', icon: '🔍' },
    { v: 'awarded', l: 'Tildelt', icon: '✅' },
    { v: 'draft', l: 'Kladder', icon: '📝' },
    { v: 'all', l: 'Alle', icon: '≡' },
  ];

  const canCreate = ['fleetos_admin', 'org_admin'].includes(user?.role) && ['fleetos', 'municipality'].includes(user?.org_type);

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Udbud</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{tenders.length} udbud fundet</p>
        </div>
        {canCreate && <Btn icon="+" onClick={onNew} size="lg">Opret udbud</Btn>}
      </div>

      {/* Filterknappar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {filters.map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              color: filter === f.v ? 'var(--accent)' : 'var(--text-2)',
              fontWeight: filter === f.v ? 700 : 500, fontSize: 13, fontFamily: 'inherit',
              borderBottom: filter === f.v ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <span>{f.icon}</span>{f.l}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : tenders.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Ingen udbud her</div>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 20 }}>Der er ingen udbud i denne kategori endnu</p>
          {canCreate && <Btn onClick={onNew}>Opret det første udbud</Btn>}
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tenders.map(t => {
            const days = daysLeft(t.deadline_bid);
            return (
              <Card key={t.id} hover onClick={() => onSelect(t)} style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <Badge status={t.status} />
                      <span style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 20, border: '1px solid var(--border)' }}>{TENDER_TYPE_LABEL[t.tender_type]}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>#{t.tender_number}</span>
                    </div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--text)', letterSpacing: '-0.01em' }}>{t.title}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>📍 {t.municipality || t.geographic_area || '—'}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>👤 {PASSENGER_LABEL[t.passenger_type] || t.passenger_type}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>🚗 {fmt(t.estimated_trips_month)} ture/md</span>
                      {t.budget_max_per_km && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Maks. {t.budget_max_per_km} kr/km</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 30, fontWeight: 900, color: 'var(--accent)', letterSpacing: '-0.04em', lineHeight: 1 }}>{t.bid_count || 0}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>tilbud</div>
                    {days !== null && t.status === 'open' && (
                      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: days <= 3 ? 'var(--red)' : days <= 7 ? 'var(--orange)' : 'var(--green)', background: (days <= 3 ? 'var(--red-bg)' : days <= 7 ? 'var(--orange-bg)' : 'var(--green-bg)'), padding: '3px 8px', borderRadius: 20 }}>
                        {days > 0 ? `${days}d tilbage` : 'Frist overskredet'}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TENDER DETAIL
// ══════════════════════════════════════════════════════════════════════════════

function TenderDetail({ tender: init, onBack }) {
  const { user } = useAuth();
  const [tender, setTender] = useState(init);
  const [tab, setTab] = useState('details');
  const [showBidForm, setShowBidForm] = useState(false);
  const [bidSent, setBidSent] = useState(false);
  const [bid, setBid] = useState({ company_name: user?.org_name || '', contact_name: user?.name || '', contact_email: user?.email || '', price_per_km: '', price_per_trip: '', vehicle_count: 1, has_taxi_permit: true, has_gps: true, has_insurance: true, years_experience: '', references_count: '', pitch: '' });
  const [question, setQuestion] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = () => api(`/tenders/${tender.id}`).then(setTender);
  const days = daysLeft(tender.deadline_bid);
  const canManage = user?.role === 'fleetos_admin' || (user?.role === 'org_admin' && user?.org_type === 'municipality');
  const canBid = ['carrier', 'transport_office'].includes(user?.org_type);

  const submitBid = async () => {
    if (!bid.price_per_km) { alert('Angiv pris per km'); return; }
    setSaving(true);
    const r = await api(`/tenders/${tender.id}/bids`, { method: 'POST', body: { ...bid, price_per_km: parseFloat(bid.price_per_km), price_per_trip: parseFloat(bid.price_per_trip) || null, vehicle_count: parseInt(bid.vehicle_count), years_experience: parseInt(bid.years_experience) || 0 } });
    if (r.id) { setBidSent(true); setShowBidForm(false); reload(); }
    else alert(r.error || 'Fejl');
    setSaving(false);
  };

  const awardBid = async bidId => {
    if (!confirm('Tildel kontrakt?')) return;
    await api(`/tenders/${tender.id}/award/${bidId}`, { method: 'POST' });
    reload();
  };

  const publish = async () => {
    if (!confirm('Publicer udbuddet?')) return;
    await api(`/tenders/${tender.id}/publish`, { method: 'POST' });
    reload();
  };

  const tabs = [
    { id: 'details', label: 'Detaljer' },
    { id: 'bids', label: `Tilbud (${tender.bids?.length || 0})` },
    { id: 'qa', label: `Q&A (${tender.qa?.length || 0})` },
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Tilbage til udbud
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Badge status={tender.status} />
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>#{tender.tender_number} · {TENDER_TYPE_LABEL[tender.tender_type]}</span>
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.2 }}>{tender.title}</h1>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {tender.status === 'draft' && canManage && <Btn variant="success" onClick={publish} icon="🚀">Publicer</Btn>}
            {tender.status === 'open' && canBid && !bidSent && !showBidForm && <Btn onClick={() => setShowBidForm(true)} icon="📝">Afgiv tilbud</Btn>}
            {bidSent && <div style={{ color: 'var(--green)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>✓ Tilbud indsendt</div>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? 'var(--accent)' : 'var(--text-2)', borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { title: '📋 Udbudsinformation', rows: [['Udbyder', tender.created_by_name], ['Kommune', tender.municipality], ['Type', TENDER_TYPE_LABEL[tender.tender_type]], ['Passagertype', PASSENGER_LABEL[tender.passenger_type]], ['Område', tender.geographic_area]] },
            { title: '📊 Kørselsbehov', rows: [['Ture/md', fmt(tender.estimated_trips_month)], ['Km/md', tender.estimated_km_month ? `${fmt(tender.estimated_km_month)} km` : null], ['Maks. pris/km', tender.budget_max_per_km ? `${tender.budget_max_per_km} kr` : null], ['Responskrav', `${tender.response_time_minutes || 15} min`]] },
          ].map(sec => (
            <Card key={sec.title}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: 'var(--text)' }}>{sec.title}</div>
              {sec.rows.map(([label, val]) => val && (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{val}</span>
                </div>
              ))}
              {sec.description && <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>{tender.description}</p>}
            </Card>
          ))}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📅 Tidsfrister</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}><span style={{ color: 'var(--text-2)' }}>Frist for bud</span><span style={{ fontWeight: 600, color: days && days <= 7 ? 'var(--orange)' : 'var(--text)' }}>{fmtDate(tender.deadline_bid)}</span></div>
            {days !== null && tender.status === 'open' && (
              <div style={{ background: days <= 3 ? 'var(--red-bg)' : days <= 7 ? 'var(--orange-bg)' : 'var(--green-bg)', border: `1px solid ${days <= 3 ? 'var(--red)' : days <= 7 ? 'var(--orange)' : 'var(--green)'}`, borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 12, fontSize: 13, fontWeight: 600, color: days <= 3 ? 'var(--red)' : days <= 7 ? 'var(--orange)' : 'var(--green)' }}>
                {days > 0 ? `⏱ ${days} dage tilbage` : '⚠️ Frist overskredet'}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}><span style={{ color: 'var(--text-2)' }}>Kontraktstart</span><span>{fmtDate(tender.contract_start)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--text-2)' }}>Kontraktslut</span><span>{fmtDate(tender.contract_end)}</span></div>
          </Card>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>✅ Krav til bydende</div>
            {[['Taxitilladelse', tender.require_taxi_permit], ['GPS-system', tender.require_gps], ['Forsikring', tender.require_insurance]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 13 }}>
                <span style={{ color: v ? 'var(--green)' : 'var(--text-3)', fontSize: 16 }}>{v ? '✓' : '○'}</span>
                <span style={{ color: v ? 'var(--text)' : 'var(--text-3)' }}>{l}</span>
              </div>
            ))}
          </Card>
          {tender.description && (
            <Card style={{ gridColumn: '1/-1' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>📝 Beskrivelse</div>
              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7 }}>{tender.description}</p>
            </Card>
          )}
          {tender.status === 'awarded' && (
            <Card style={{ gridColumn: '1/-1', border: '1px solid var(--green)', background: 'var(--green-bg)' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--green)', marginBottom: 4 }}>✅ Kontrakt tildelt</div>
              <p style={{ fontSize: 14, color: 'var(--text)' }}>Tildelt til: <strong>{tender.awarded_to_name}</strong> — {fmtDate(tender.awarded_at)}</p>
            </Card>
          )}
        </div>
      )}

      {tab === 'bids' && (
        <div>
          {showBidForm && (
            <Card style={{ marginBottom: 20, border: '1px solid var(--accent)' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>📝 Afgiv tilbud</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                <Input label="Firmanavn" value={bid.company_name} onChange={v => setBid({ ...bid, company_name: v })} required />
                <Input label="Kontaktperson" value={bid.contact_name} onChange={v => setBid({ ...bid, contact_name: v })} />
                <Input label="Email" value={bid.contact_email} onChange={v => setBid({ ...bid, contact_email: v })} type="email" />
                <Input label="Telefon" value={bid.contact_phone} onChange={v => setBid({ ...bid, contact_phone: v })} />
                <Input label="Pris per km (kr)" value={bid.price_per_km} onChange={v => setBid({ ...bid, price_per_km: v })} type="number" required />
                <Input label="Pris per tur (kr)" value={bid.price_per_trip} onChange={v => setBid({ ...bid, price_per_trip: v })} type="number" />
                <Input label="Antal vogne" value={bid.vehicle_count} onChange={v => setBid({ ...bid, vehicle_count: v })} type="number" />
                <Input label="Års erfaring" value={bid.years_experience} onChange={v => setBid({ ...bid, years_experience: v })} type="number" />
              </div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
                {[['has_taxi_permit', 'Taxitilladelse'], ['has_gps', 'GPS-system'], ['has_insurance', 'Forsikring']].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: 'var(--text-2)' }}>
                    <input type="checkbox" checked={bid[k]} onChange={e => setBid({ ...bid, [k]: e.target.checked })} />
                    {l}
                  </label>
                ))}
              </div>
              <Textarea label="Pitch / salgsargument" value={bid.pitch} onChange={v => setBid({ ...bid, pitch: v })} rows={3} placeholder="Beskriv hvorfor I er det bedste valg..." />
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn onClick={submitBid} disabled={saving}>{saving ? <><Spinner /> Sender...</> : 'Indsend tilbud'}</Btn>
                <Btn variant="secondary" onClick={() => setShowBidForm(false)}>Annuller</Btn>
              </div>
            </Card>
          )}
          {!tender.bids?.length ? (
            <Card style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
              <p style={{ color: 'var(--text-2)' }}>Ingen tilbud modtaget endnu</p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tender.bids?.map((b, i) => (
                <Card key={b.id} style={{ border: b.status === 'accepted' ? '1px solid var(--green)' : '1px solid var(--border)', opacity: b.status === 'rejected' ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        {i === 0 && b.status === 'submitted' && <span style={{ background: 'var(--orange-bg)', color: 'var(--orange)', border: '1px solid var(--orange)', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>🥇 Bedst scoret</span>}
                        {b.status === 'accepted' && <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>✅ Vinder</span>}
                        {b.status === 'rejected' && <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Ikke valgt</span>}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{b.company_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3 }}>{b.contact_name} {b.contact_email && `· ${b.contact_email}`}</div>
                    </div>
                    {b.score_total && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 30, fontWeight: 900, color: b.score_total >= 80 ? 'var(--green)' : b.score_total >= 60 ? 'var(--orange)' : 'var(--red)', letterSpacing: '-0.04em', lineHeight: 1 }}>
                          {Math.round(b.score_total)}<span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 400 }}>/100</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>score</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    {[['Pris/km', b.price_per_km ? `${b.price_per_km} kr` : '—'], ['Pris/tur', b.price_per_trip ? `${b.price_per_trip} kr` : '—'], ['Vogne', b.vehicle_count || '—'], ['Erfaring', b.years_experience ? `${b.years_experience} år` : '—']].map(([l, v]) => (
                      <StatBox key={l} label={l} value={v} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12 }}>
                    {b.has_taxi_permit && <span style={{ color: 'var(--green)' }}>✓ Taxitilladelse</span>}
                    {b.has_gps && <span style={{ color: 'var(--green)' }}>✓ GPS</span>}
                    {b.has_insurance && <span style={{ color: 'var(--green)' }}>✓ Forsikring</span>}
                  </div>
                  {b.pitch && <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-2)', fontStyle: 'italic', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 12 }}>"{b.pitch}"</p>}
                  {canManage && tender.status === 'open' && b.status === 'submitted' && (
                    <div style={{ marginTop: 14 }}><Btn variant="success" size="sm" onClick={() => awardBid(b.id)}>🏆 Tildel kontrakt</Btn></div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'qa' && (
        <div>
          {tender.qa?.map(q => (
            <Card key={q.id} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>❓ {q.question}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>— {q.asked_by}, {fmtDate(q.asked_at)}</div>
              {q.answer ? (
                <div style={{ background: 'var(--green-bg)', borderLeft: '3px solid var(--green)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 13, color: 'var(--text)' }}>{q.answer}</div>
              ) : <span style={{ fontSize: 13, color: 'var(--orange)' }}>⏳ Afventer svar</span>}
            </Card>
          ))}
          {tender.status === 'open' && (
            <Card style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>💬 Stil et spørgsmål</div>
              <Textarea value={question} onChange={setQuestion} placeholder="Dit spørgsmål til udbyderen..." rows={3} />
              <Btn onClick={async () => { if (!question.trim()) return; await api(`/tenders/${tender.id}/qa`, { method: 'POST', body: { question, asked_by: user?.name || 'Anonym' } }); setQuestion(''); reload(); }} disabled={!question.trim()}>Send spørgsmål</Btn>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CREATE TENDER
// ══════════════════════════════════════════════════════════════════════════════

function CreateTender({ onBack, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ created_by_type: user?.org_type || 'municipality', created_by_name: user?.org_name || '', created_by_email: user?.email || '', municipality: '', title: '', description: '', geographic_area: '', estimated_trips_month: '', estimated_km_month: '', passenger_type: 'elderly', tender_type: 'standard', contract_start: '', contract_end: '', deadline_bid: '', budget_max_per_km: '', budget_max_per_trip: '', response_time_minutes: 15, min_vehicles: 1, require_taxi_permit: true, require_gps: true, require_insurance: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (publish = false) => {
    if (!form.title || !form.created_by_name) { alert('Titel og udbyderens navn er påkrævet'); return; }
    setSaving(true);
    const r = await api('/tenders', { method: 'POST', body: { ...form, estimated_trips_month: parseInt(form.estimated_trips_month) || null, estimated_km_month: parseFloat(form.estimated_km_month) || null, budget_max_per_km: parseFloat(form.budget_max_per_km) || null, budget_max_per_trip: parseFloat(form.budget_max_per_trip) || null } });
    if (r.id) {
      if (publish) await api(`/tenders/${r.id}/publish`, { method: 'POST' });
      onCreated();
    } else alert(r.error || 'Fejl ved oprettelse');
    setSaving(false);
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>← Tilbage</button>
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em' }}>Opret nyt udbud</h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>👤 Udbyder</div>
          <Input label="Navn / organisation" value={form.created_by_name} onChange={v => set('created_by_name', v)} required />
          <Input label="Email" value={form.created_by_email} onChange={v => set('created_by_email', v)} type="email" />
          <Input label="Kommune / by" value={form.municipality} onChange={v => set('municipality', v)} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📋 Type</div>
          <Select label="Udbudstype" value={form.tender_type} onChange={v => set('tender_type', v)} options={[{ value: 'standard', label: 'Standard udbud' }, { value: 'framework', label: 'Rammeaftale' }, { value: 'urgent', label: 'Akutudbud' }]} />
          <Select label="Passagertype" value={form.passenger_type} onChange={v => set('passenger_type', v)} options={[{ value: 'elderly', label: 'Ældre' }, { value: 'disabled', label: 'Handicappede' }, { value: 'general', label: 'Generel' }, { value: 'school', label: 'Skolekørsel' }, { value: 'mixed', label: 'Blandet' }]} />
          <Input label="Geografisk område" value={form.geographic_area} onChange={v => set('geographic_area', v)} placeholder="fx Vordingborg Kommune" />
        </Card>
        <Card style={{ gridColumn: '1/-1' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📝 Indhold</div>
          <Input label="Titel" value={form.title} onChange={v => set('title', v)} required placeholder="fx Visiteret kørsel — ældre og handicappede 2026-2028" />
          <Textarea label="Beskrivelse" value={form.description} onChange={v => set('description', v)} rows={4} placeholder="Beskriv udbuddet i detaljer..." />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📊 Økonomi & kørsel</div>
          <Input label="Estimerede ture/md" value={form.estimated_trips_month} onChange={v => set('estimated_trips_month', v)} type="number" />
          <Input label="Estimerede km/md" value={form.estimated_km_month} onChange={v => set('estimated_km_month', v)} type="number" />
          <Input label="Maks. pris/km (kr)" value={form.budget_max_per_km} onChange={v => set('budget_max_per_km', v)} type="number" />
          <Input label="Maks. pris/tur (kr)" value={form.budget_max_per_trip} onChange={v => set('budget_max_per_trip', v)} type="number" />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>📅 Tidsfrister</div>
          <Input label="Frist for bud" value={form.deadline_bid} onChange={v => set('deadline_bid', v)} type="datetime-local" />
          <Input label="Kontraktstart" value={form.contract_start} onChange={v => set('contract_start', v)} type="date" />
          <Input label="Kontraktslut" value={form.contract_end} onChange={v => set('contract_end', v)} type="date" />
          <Input label="Responskrav (min)" value={form.response_time_minutes} onChange={v => set('response_time_minutes', v)} type="number" />
          <Input label="Min. antal vogne" value={form.min_vehicles} onChange={v => set('min_vehicles', v)} type="number" />
        </Card>
        <Card style={{ gridColumn: '1/-1' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>✅ Krav til bydende</div>
          <div style={{ display: 'flex', gap: 24 }}>
            {[['require_taxi_permit', 'Taxitilladelse påkrævet'], ['require_gps', 'GPS påkrævet'], ['require_insurance', 'Forsikring påkrævet']].map(([k, l]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', color: 'var(--text-2)' }}>
                <input type="checkbox" checked={form[k]} onChange={e => set(k, e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
                {l}
              </label>
            ))}
          </div>
        </Card>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Btn variant="success" size="lg" onClick={() => save(true)} disabled={saving} icon="🚀">Opret og publicer</Btn>
        <Btn variant="secondary" size="lg" onClick={() => save(false)} disabled={saving}>Gem som kladde</Btn>
        <Btn variant="ghost" size="lg" onClick={onBack}>Annuller</Btn>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEAMS & ORGANISATIONER (stub — udvides)
// ══════════════════════════════════════════════════════════════════════════════

function TeamsView() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [nu, setNu] = useState({ name: '', email: '', role: 'case_handler', password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.org_id) {
      api(`/udbud/organisations/${user.org_id}/users`).then(d => { setUsers(Array.isArray(d) ? d : []); setLoading(false); });
    }
  }, [user]);

  const addUser = async () => {
    setSaving(true);
    const r = await api(`/udbud/organisations/${user.org_id}/users`, { method: 'POST', body: nu });
    if (r.ok) { setShowAdd(false); setNu({ name: '', email: '', role: 'case_handler', password: '' }); api(`/udbud/organisations/${user.org_id}/users`).then(d => setUsers(Array.isArray(d) ? d : [])); }
    else alert(r.error || 'Fejl');
    setSaving(false);
  };

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Team</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{users.length} brugere i din organisation</p>
        </div>
        <Btn icon="+" onClick={() => setShowAdd(true)}>Inviter bruger</Btn>
      </div>

      {showAdd && (
        <Card style={{ marginBottom: 20, border: '1px solid var(--accent)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>👤 Inviter ny bruger</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Input label="Navn" value={nu.name} onChange={v => setNu({ ...nu, name: v })} required />
            <Input label="Email" value={nu.email} onChange={v => setNu({ ...nu, email: v })} type="email" required />
            <Select label="Rolle" value={nu.role} onChange={v => setNu({ ...nu, role: v })} options={[{ value: 'case_handler', label: '📋 Sagsbehandler' }, { value: 'reader', label: '👁 Læser' }]} />
            <Input label="Midlertidig adgangskode" value={nu.password} onChange={v => setNu({ ...nu, password: v })} type="password" hint="Lad stå tomt for auto-genereret" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={addUser} disabled={saving}>{saving ? <><Spinner /> Opretter...</> : 'Opret og send invitation'}</Btn>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Annuller</Btn>
          </div>
        </Card>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2].map(i => <div key={i} className="skeleton" style={{ height: 72, borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map(u => {
            const r = ROLE_INFO[u.role] || {};
            return (
              <Card key={u.id} style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--accent-bg)', border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{u.email}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{r.icon} {r.label}</span>
                    <span style={{ fontSize: 11, color: u.is_active ? 'var(--green)' : 'var(--red)', background: u.is_active ? 'var(--green-bg)' : 'var(--red-bg)', padding: '2px 8px', borderRadius: 20 }}>{u.is_active ? 'Aktiv' : 'Inaktiv'}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Sidst set: {u.last_login ? fmtDate(u.last_login) : 'Aldrig'}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConsortiaView() {
  const { user } = useAuth();
  const [consortia, setConsortia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', invite_emails: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/udbud/consortia').then(d => { setConsortia(Array.isArray(d) ? d : []); setLoading(false); });
  }, []);

  const create = async () => {
    setSaving(true);
    const emails = form.invite_emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes('@'));
    const r = await api('/udbud/consortia', { method: 'POST', body: { name: form.name, invite_emails: emails, notes: form.notes } });
    if (r.ok) { setShowNew(false); api('/udbud/consortia').then(d => setConsortia(Array.isArray(d) ? d : [])); }
    else alert(r.error || 'Fejl');
    setSaving(false);
  };

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Konsortier</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Slå jer sammen med andre vognmænd om store udbud</p>
        </div>
        {['carrier', 'transport_office', 'fleetos'].includes(user?.org_type) && <Btn icon="+" onClick={() => setShowNew(true)}>Opret konsortium</Btn>}
      </div>

      {showNew && (
        <Card style={{ marginBottom: 20, border: '1px solid var(--accent)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>🤝 Nyt konsortium</div>
          <Input label="Konsortiets navn" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="fx Sydsjælland Transport Konsortium" required />
          <Textarea label="Inviter partnere (email pr. linje)" value={form.invite_emails} onChange={v => setForm({ ...form, invite_emails: v })} placeholder="partner1@firma.dk&#10;partner2@firma.dk" rows={3} />
          <Textarea label="Interne noter" value={form.notes} onChange={v => setForm({ ...form, notes: v })} rows={2} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={create} disabled={saving || !form.name}>{saving ? <><Spinner /> Opretter...</> : 'Opret og send invitationer'}</Btn>
            <Btn variant="secondary" onClick={() => setShowNew(false)}>Annuller</Btn>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="skeleton" style={{ height: 100, borderRadius: 'var(--radius-lg)' }} />
      ) : consortia.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Ingen konsortier endnu</div>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 20 }}>Opret et konsortium og inviter andre vognmænd til at byde med dig</p>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {consortia.map(c => (
            <Card key={c.id} hover style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🤝</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>Lead: {c.lead_org_name} · {c.member_count} {parseInt(c.member_count) === 1 ? 'medlem' : 'medlemmer'}</div>
                </div>
                <span style={{ fontSize: 12, color: c.status === 'ready' ? 'var(--green)' : 'var(--orange)', background: c.status === 'ready' ? 'var(--green-bg)' : 'var(--orange-bg)', padding: '4px 12px', borderRadius: 20, fontWeight: 600 }}>
                  {c.status === 'forming' ? 'Under dannelse' : c.status === 'ready' ? 'Klar' : c.status}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoicesView() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/udbud/invoices').then(d => { setInvoices(Array.isArray(d) ? d : []); setLoading(false); });
  }, []);

  const statusColor = { draft: 'var(--text-3)', sent: 'var(--orange)', paid: 'var(--green)', overdue: 'var(--red)' };
  const statusLabel = { draft: 'Kladde', sent: 'Sendt', paid: 'Betalt', overdue: 'Forfalden' };

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Fakturaer</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>EAN-klar til kommuner · {invoices.length} fakturaer</p>
        </div>
      </div>
      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
      ) : invoices.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Ingen fakturaer endnu</div>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Fakturaer oprettes automatisk når kommuner indgår kontrakter</p>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {invoices.map(inv => (
            <Card key={inv.id} hover style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700, fontSize: 14, minWidth: 140 }}>{inv.invoice_number}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{inv.org_name}</div>
                  {inv.ean_number && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>EAN: {inv.ean_number}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>{fmt(inv.total)} kr</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>inkl. moms</div>
                </div>
                <span style={{ background: statusColor[inv.status] + '18', color: statusColor[inv.status], border: `1px solid ${statusColor[inv.status]}30`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                  {statusLabel[inv.status]}
                </span>
                <div style={{ fontSize: 12, color: 'var(--text-3)', minWidth: 80 }}>Forfald: {fmtDate(inv.due_date)}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function OrganisationsView() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/udbud/organisations').then(d => { setOrgs(Array.isArray(d) ? d : []); setLoading(false); });
  }, []);

  const orgIcon = { municipality: '🏛️', carrier: '🚕', transport_office: '🏢', fleetos: '⚙️' };
  const orgLabel = { municipality: 'Kommune', carrier: 'Vognmand', transport_office: 'Kørselskontor', fleetos: 'FleetOS' };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4 }}>Organisationer</h1>
        <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{orgs.length} organisationer i systemet</p>
      </div>
      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {orgs.map(o => (
            <Card key={o.id} hover style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{orgIcon[o.org_type]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{o.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{o.contact_email} {o.cvr && `· CVR: ${o.cvr}`} {o.ean_number && `· EAN: ${o.ean_number}`}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--bg-3)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 20 }}>{orgLabel[o.org_type]} · {o.user_count} brugere</span>
                  <span style={{ fontSize: 11, background: o.status === 'active' ? 'var(--green-bg)' : 'var(--orange-bg)', color: o.status === 'active' ? 'var(--green)' : 'var(--orange)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                    {o.status === 'active' ? 'Aktiv' : o.status === 'pending' ? 'Afventer' : 'Suspenderet'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════════════════════

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('tenders');
  const [selectedTender, setSelectedTender] = useState(null);
  const [showTheme, setShowTheme] = useState(false);
  const [activeTheme, setActiveTheme] = useState('dark-pro');
  const [activeFont, setActiveFont] = useState('normal');

  // Load theme on mount
  useEffect(() => {
    const { theme, fontSize } = loadUserTheme();
    setActiveTheme(theme);
    setActiveFont(fontSize);
  }, []);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('udbud_token');
    if (token) {
      api('/udbud/auth/me').then(r => {
        if (r.id) setUser(r);
        else localStorage.removeItem('udbud_token');
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleAuth = u => setUser(u);
  const handleLogout = () => { localStorage.removeItem('udbud_token'); setUser(null); };

  const themeCtx = {
    activeTheme, activeFont,
    setTheme: key => { setActiveTheme(key); saveUserTheme(key, activeFont); },
    setFont: key => { setActiveFont(key); saveUserTheme(activeTheme, key); },
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <Spinner />
      <span style={{ color: 'var(--text-3)', fontSize: 14 }}>Indlæser...</span>
    </div>
  );

  if (!user) return (
    <ThemeContext.Provider value={themeCtx}>
      <AuthScreen onAuth={handleAuth} />
    </ThemeContext.Provider>
  );

  return (
    <ThemeContext.Provider value={themeCtx}>
      <AuthContext.Provider value={{ user, logout: handleLogout }}>
        <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
          <Topbar activeView={view} setView={v => { setView(v); setSelectedTender(null); }} showTheme={showTheme} setShowTheme={setShowTheme} />

          {showTheme && <ThemePicker onClose={() => setShowTheme(false)} />}

          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px' }}>
            {view === 'tenders' && !selectedTender && <TenderList onSelect={t => setSelectedTender(t)} onNew={() => setView('create')} />}
            {view === 'tenders' && selectedTender && <TenderDetail tender={selectedTender} onBack={() => setSelectedTender(null)} />}
            {view === 'create' && <CreateTender onBack={() => setView('tenders')} onCreated={() => setView('tenders')} />}
            {view === 'organisations' && <OrganisationsView />}
            {view === 'consortia' && <ConsortiaView />}
            {view === 'invoices' && <InvoicesView />}
            {view === 'team' && <TeamsView />}
          </div>
        </div>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}
